import { useState, useEffect } from 'preact/hooks';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Bot, 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Settings2,
  Code,
  Brain,
  Shield,
  Zap,
  FileText,
  Save,
  Eye,
  EyeOff,
  Copy,
  Download,
  Upload,
  Search,
  RefreshCw,
  Clock,
  User,
  Folder,
  FolderOpen,
  AlertCircle,
  CheckCircle
} from 'lucide-preact';

interface Subagent {
  id: string;
  name: string;
  description: string;
  tools: string[];
  systemPrompt: string;
  type: 'project' | 'user';
  filename: string;
  createdAt: number;
  updatedAt: number;
  isActive: boolean;
}

interface SubagentManagerProps {
  sessionId?: string;
  onSubagentCreate: (subagent: Omit<Subagent, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onSubagentUpdate: (id: string, subagent: Partial<Subagent>) => Promise<void>;
  onSubagentDelete: (id: string) => Promise<void>;
  onSubagentExecute: (id: string, prompt: string) => Promise<void>;
}

const AVAILABLE_TOOLS = [
  'Read', 'Write', 'Edit', 'MultiEdit', 'Bash', 'Grep', 'Glob', 
  'WebSearch', 'WebFetch', 'NotebookEdit', 'TodoWrite', 'Task',
  'ExitPlanMode', 'ListMcpResources', 'ReadMcpResource'
];

const EXAMPLE_SUBAGENTS = [
  {
    name: 'code-reviewer',
    description: 'Expert code review specialist. Proactively reviews code for quality, security, and maintainability.',
    tools: ['Read', 'Grep', 'Glob', 'Bash'],
    systemPrompt: `You are a senior code reviewer ensuring high standards of code quality and security.

When invoked:
1. Run git diff to see recent changes
2. Focus on modified files
3. Begin review immediately

Review checklist:
- Code is simple and readable
- Functions and variables are well-named
- No duplicated code
- Proper error handling
- No exposed secrets or API keys
- Input validation implemented
- Good test coverage
- Performance considerations addressed

Provide feedback organized by priority:
- Critical issues (must fix)
- Warnings (should fix)
- Suggestions (consider improving)

Include specific examples of how to fix issues.`
  },
  {
    name: 'debugger',
    description: 'Debugging specialist for errors, test failures, and unexpected behavior. Use proactively when encountering any issues.',
    tools: ['Read', 'Edit', 'Bash', 'Grep', 'Glob'],
    systemPrompt: `You are an expert debugger specializing in root cause analysis.

When invoked:
1. Capture error message and stack trace
2. Identify reproduction steps
3. Isolate the failure location
4. Implement minimal fix
5. Verify solution works

Debugging process:
- Analyze error messages and logs
- Check recent code changes
- Form and test hypotheses
- Add strategic debug logging
- Inspect variable states

For each issue, provide:
- Root cause explanation
- Evidence supporting the diagnosis
- Specific code fix
- Testing approach
- Prevention recommendations

Focus on fixing the underlying issue, not just symptoms.`
  },
  {
    name: 'test-runner',
    description: 'Test automation expert. Use proactively to run tests and fix failures.',
    tools: ['Read', 'Edit', 'Bash', 'Grep'],
    systemPrompt: `You are a test automation expert. When you see code changes, proactively run the appropriate tests. If tests fail, analyze the failures and fix them while preserving the original test intent.

Key responsibilities:
- Identify and run relevant tests after code changes
- Analyze test failures and determine root causes
- Fix failing tests while maintaining test integrity
- Suggest additional test coverage when needed
- Ensure tests are fast and reliable

Process:
1. Detect code changes (git diff, file modifications)
2. Identify affected test files
3. Run tests and capture results
4. Analyze failures and implement fixes
5. Verify fixes work and don't break other tests
6. Suggest improvements to test coverage

Focus on maintaining test quality while fixing issues.`
  },
  {
    name: 'documentation-writer',
    description: 'Technical documentation specialist. Creates clear, comprehensive documentation.',
    tools: ['Read', 'Write', 'Edit', 'Grep'],
    systemPrompt: `You are a technical documentation specialist focused on creating clear, comprehensive documentation.

When invoked:
1. Analyze the codebase and identify documentation gaps
2. Create or update documentation as needed
3. Ensure documentation is accurate and up-to-date

Documentation standards:
- Clear, concise language
- Proper code examples
- Step-by-step instructions
- API documentation with parameters and return values
- README files with setup and usage instructions
- Architecture diagrams when helpful

Focus on making the codebase accessible to new developers and users.`
  }
];

export default function SubagentManager({ 
  sessionId, 
  onSubagentCreate, 
  onSubagentUpdate, 
  onSubagentDelete, 
  onSubagentExecute 
}: SubagentManagerProps) {
  const [subagents, setSubagents] = useState<Subagent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSubagent, setEditingSubagent] = useState<Subagent | null>(null);
  const [executingSubagent, setExecutingSubagent] = useState<string | null>(null);
  const [executionPrompt, setExecutionPrompt] = useState('');
  const [showYamlPreview, setShowYamlPreview] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tools: [] as string[],
    systemPrompt: '',
    type: 'project' as 'project' | 'user'
  });

  // Load subagents from filesystem
  const loadSubagents = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/subagents');
      if (!response.ok) {
        throw new Error('Failed to load subagents');
      }
      const data = await response.json();
      setSubagents(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subagents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubagents();
  }, []);

  const handleCreateSubagent = async () => {
    try {
      const response = await fetch('/api/subagents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subagent');
      }

      await loadSubagents();
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        tools: [],
        systemPrompt: '',
        type: 'project'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create subagent');
    }
  };

  const handleUpdateSubagent = async (subagent: Subagent) => {
    try {
      const response = await fetch(`/api/subagents/${subagent.name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: subagent.description,
          tools: subagent.tools,
          systemPrompt: subagent.systemPrompt,
          type: subagent.type
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update subagent');
      }

      await loadSubagents();
      setEditingSubagent(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subagent');
    }
  };

  const handleDeleteSubagent = async (subagent: Subagent) => {
    if (!confirm(`Are you sure you want to delete the subagent "${subagent.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/subagents/${subagent.name}?type=${subagent.type}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete subagent');
      }

      await loadSubagents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete subagent');
    }
  };

  const handleExecuteSubagent = async (subagent: Subagent) => {
    if (!executionPrompt.trim()) {
      setError('Please enter a prompt for the subagent');
      return;
    }

    try {
      setExecutingSubagent(subagent.id);
      await onSubagentExecute(subagent.id, executionPrompt);
      setExecutionPrompt('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute subagent');
    } finally {
      setExecutingSubagent(null);
    }
  };

  const loadExampleSubagent = (example: typeof EXAMPLE_SUBAGENTS[0]) => {
    setFormData({
      name: example.name,
      description: example.description,
      tools: example.tools,
      systemPrompt: example.systemPrompt,
      type: 'project'
    });
    setShowCreateForm(true);
  };

  const generateYamlPreview = (data: typeof formData) => {
    return `---
name: ${data.name}
description: ${data.description}
${data.tools.length > 0 ? `tools: ${data.tools.join(', ')}` : ''}
---

${data.systemPrompt}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading subagents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Subagents</h2>
          <p className="text-muted-foreground">
            Manage Byte DevOps subagents for specialized tasks
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Subagent
          </Button>
          <Button variant="outline" onClick={loadSubagents}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-destructive font-medium">Error</span>
          </div>
          <p className="text-destructive mt-1">{error}</p>
        </div>
      )}

      {/* Subagents List */}
      {subagents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No subagents found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first subagent to get started with specialized AI assistants
            </p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Subagent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {subagents.map((subagent) => (
            <Card key={subagent.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{subagent.name}</CardTitle>
                    <Badge variant={subagent.type === 'project' ? 'default' : 'secondary'}>
                      {subagent.type}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingSubagent(subagent)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSubagent(subagent)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardDescription className="text-sm">
                  {subagent.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Tools */}
                {subagent.tools.length > 0 && (
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Tools</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {subagent.tools.map((tool) => (
                        <Badge key={tool} variant="outline" className="text-xs">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* System Prompt Preview */}
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">System Prompt</Label>
                  <div className="mt-1 p-2 bg-muted rounded text-xs font-mono max-h-20 overflow-y-auto">
                    {subagent.systemPrompt.substring(0, 100)}
                    {subagent.systemPrompt.length > 100 && '...'}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditingSubagent(subagent)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingSubagent(subagent);
                      setExecutionPrompt('');
                    }}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Execute
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingSubagent) && (
        <Card className="fixed inset-4 z-50 overflow-auto">
          <CardHeader>
            <CardTitle>
              {editingSubagent ? 'Edit Subagent' : 'Create New Subagent'}
            </CardTitle>
            <CardDescription>
              {editingSubagent 
                ? 'Modify the subagent configuration'
                : 'Create a new specialized AI assistant'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="prompt">System Prompt</TabsTrigger>
                <TabsTrigger value="preview">YAML Preview</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={editingSubagent?.name || formData.name}
                      onChange={(e) => {
                        if (editingSubagent) {
                          setEditingSubagent({ ...editingSubagent, name: e.currentTarget.value });
                        } else {
                          setFormData({ ...formData, name: e.currentTarget.value });
                        }
                      }}
                      placeholder="e.g., code-reviewer"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select
                      value={editingSubagent?.type || formData.type}
                      onValueChange={(value: 'project' | 'user') => {
                        if (editingSubagent) {
                          setEditingSubagent({ ...editingSubagent, type: value });
                        } else {
                          setFormData({ ...formData, type: value });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project">Project-level</SelectItem>
                        <SelectItem value="user">User-level</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={editingSubagent?.description || formData.description}
                    onChange={(e) => {
                      if (editingSubagent) {
                        setEditingSubagent({ ...editingSubagent, description: e.currentTarget.value });
                      } else {
                        setFormData({ ...formData, description: e.currentTarget.value });
                      }
                    }}
                    placeholder="Brief description of when to use this subagent"
                  />
                </div>

                <div>
                  <Label>Tools</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {AVAILABLE_TOOLS.map((tool) => {
                      const isSelected = (editingSubagent?.tools || formData.tools).includes(tool);
                      return (
                        <Button
                          key={tool}
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const currentTools = editingSubagent?.tools || formData.tools;
                            const newTools = isSelected
                              ? currentTools.filter(t => t !== tool)
                              : [...currentTools, tool];
                            
                            if (editingSubagent) {
                              setEditingSubagent({ ...editingSubagent, tools: newTools });
                            } else {
                              setFormData({ ...formData, tools: newTools });
                            }
                          }}
                        >
                          {tool}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="prompt" className="space-y-4">
                <div>
                  <Label htmlFor="systemPrompt">System Prompt</Label>
                  <Textarea
                    id="systemPrompt"
                    value={editingSubagent?.systemPrompt || formData.systemPrompt}
                    onChange={(e) => {
                      if (editingSubagent) {
                        setEditingSubagent({ ...editingSubagent, systemPrompt: e.currentTarget.value });
                      } else {
                        setFormData({ ...formData, systemPrompt: e.currentTarget.value });
                      }
                    }}
                    placeholder="Enter the system prompt for this subagent..."
                    className="min-h-64 font-mono text-sm"
                  />
                </div>
              </TabsContent>

              <TabsContent value="preview" className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>YAML Preview</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const yaml = editingSubagent 
                        ? generateYamlPreview({
                            name: editingSubagent.name,
                            description: editingSubagent.description,
                            tools: editingSubagent.tools,
                            systemPrompt: editingSubagent.systemPrompt,
                            type: editingSubagent.type
                          })
                        : generateYamlPreview(formData);
                      navigator.clipboard.writeText(yaml);
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <div className="bg-muted p-4 rounded-lg">
                  <pre className="text-sm font-mono whitespace-pre-wrap">
                    {editingSubagent 
                      ? generateYamlPreview({
                          name: editingSubagent.name,
                          description: editingSubagent.description,
                          tools: editingSubagent.tools,
                          systemPrompt: editingSubagent.systemPrompt,
                          type: editingSubagent.type
                        })
                      : generateYamlPreview(formData)
                    }
                  </pre>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingSubagent(null);
                  setFormData({
                    name: '',
                    description: '',
                    tools: [],
                    systemPrompt: '',
                    type: 'project'
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={editingSubagent ? () => handleUpdateSubagent(editingSubagent) : handleCreateSubagent}
                disabled={!formData.name || !formData.description || !formData.systemPrompt}
              >
                <Save className="h-4 w-4 mr-2" />
                {editingSubagent ? 'Update' : 'Create'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Example Subagents */}
      {!showCreateForm && !editingSubagent && (
        <Card>
          <CardHeader>
            <CardTitle>Example Subagents</CardTitle>
            <CardDescription>
              Click on any example to load it into the creation form
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {EXAMPLE_SUBAGENTS.map((example) => (
                <div
                  key={example.name}
                  className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => loadExampleSubagent(example)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold">{example.name}</h4>
                    <Badge variant="outline" className="text-xs">
                      {example.tools.length} tools
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {example.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {example.tools.slice(0, 3).map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                    {example.tools.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{example.tools.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}